import { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  ChartNoAxesCombined, ChartCandlestick, WalletCards, Trophy, Copy, ChartPie,
  TrendingUp, Users,
  MessageCircleQuestionMark, Sun, Moon, Pencil, LogOut, Menu, X, ShieldCheck,
  ShieldPlus, Send, Bell,
} from 'lucide-react';
import Logo from '../../components/Logo/Logo';
import ContractModal from '../../components/ContractModal/ContractModal';
import api from '../../services/api';
import { useAnnouncements } from '../../context/AnnouncementsProvider';
import { lockScroll, unlockScroll } from '../../utils/scrollLock';
import styles from './DashboardLayout.module.css';

const ALL_NAV_ITEMS = [
  { icon: ChartPie,                  path: '/dashboard/overview',     label: 'Overview',     shortcut: 'O', adminOnly: true  },
  { icon: ChartCandlestick,          path: '/dashboard/risk-cockpit', label: 'Risk Cockpit', shortcut: 'C', adminOnly: true  },
  { icon: ChartNoAxesCombined,       path: '/dashboard/assets',       label: 'Assets',       shortcut: 'A', adminOnly: false },
  { icon: WalletCards,               path: '/dashboard/wallet',       label: 'Wallet',       shortcut: 'W', adminOnly: false },
  { icon: Trophy,                    path: '/dashboard/leaderboard',  label: 'Leaderboard',  shortcut: 'L', adminOnly: false },
  { icon: Copy,                      path: '/dashboard/trade-copier', label: 'Trade Copier', shortcut: 'T', adminOnly: false },
  { icon: Users,                     path: '/dashboard/team',         label: 'Team',         shortcut: 'M', adminOnly: true  },
  { icon: MessageCircleQuestionMark, path: '/dashboard/help',         label: 'Help Center',  shortcut: 'H', adminOnly: false },
];

/* Lives with the theme toggle and avatar at the foot of the sidebar rather
   than in the nav list, so it is kept out of ALL_NAV_ITEMS and folded back in
   wherever the whole set matters (shortcuts, the mobile menu). */
const ANNOUNCEMENTS_ITEM = {
  icon: Bell, path: '/dashboard/announcements', label: 'Announcements', shortcut: 'N', adminOnly: false,
};

function getInitialTheme() {
  const saved = localStorage.getItem('theme');
  if (saved) return saved;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function DashboardLayout({ children, className }) {
  const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
  const { isAdmin, firstName = '', lastName = '', email: userEmail = '' } = storedUser;
  const isRecoverySession = localStorage.getItem('isRecoverySession') === 'true';
  const navItems = ALL_NAV_ITEMS.filter(item => isAdmin || !item.adminOnly);
  const { unreadCount } = useAnnouncements();

  /* Show contract modal for non-admin users who haven't signed yet */
  const [showContract, setShowContract] = useState(
    () => !isAdmin && !storedUser.contractSigned
  );
  const fullName  = [firstName, lastName].filter(Boolean).join(' ') || 'User';
  const initial   = firstName?.[0]?.toUpperCase() || 'U';

  const [popoverOpen, setPopoverOpen]       = useState(false);
  const [menuOpen, setMenuOpen]             = useState(false);
  const [theme, setTheme]                   = useState(getInitialTheme);
  const [editingName, setEditingName]       = useState(false);
  const [nameValue, setNameValue]           = useState(fullName);
  const [recoveryOpen, setRecoveryOpen]         = useState(false);
  const [recoveryEmail, setRecoveryEmail]       = useState('');
  const [recoveryError, setRecoveryError]       = useState('');
  const [savedRecovery, setSavedRecovery]       = useState(() => storedUser.recoveryContactEmail || '');
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);
  const popoverRef = useRef(null);
  const nameInputRef = useRef(null);
  const navigate = useNavigate();

  async function saveName() {
    setEditingName(false);
    const trimmed = nameValue.trim();
    if (!trimmed || trimmed === fullName) return;
    const parts     = trimmed.split(' ');
    const firstName = parts[0] || '';
    const lastName  = parts.slice(1).join(' ') || '';
    try {
      await api.patch('/auth/me', { firstName, lastName });
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      localStorage.setItem('user', JSON.stringify({ ...u, firstName, lastName }));
    } catch (err) {
      console.error('Failed to save name:', err);
      setNameValue(fullName); // revert on error
    }
  }

  function openRecovery() {
    setPopoverOpen(false);
    setRecoveryEmail(savedRecovery);
    setRecoveryError('');
    setRecoveryOpen(true);
  }
  async function saveRecovery() {
    if (!recoveryEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recoveryEmail)) {
      setRecoveryError('Please enter a valid email address.');
      return;
    }
    try {
      await api.patch('/auth/me/recovery', { recoveryEmail: recoveryEmail.trim() });
      setSavedRecovery(recoveryEmail.trim());
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      localStorage.setItem('user', JSON.stringify({ ...u, recoveryContactEmail: recoveryEmail.trim() }));
      setRecoveryOpen(false);
    } catch (err) {
      setRecoveryError(err.response?.data?.error || 'Failed to save. Please try again.');
    }
  }
  async function removeRecovery() {
    try {
      await api.delete('/auth/me/recovery');
      setSavedRecovery('');
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      localStorage.setItem('user', JSON.stringify({ ...u, recoveryContactEmail: '' }));
      setRemoveConfirmOpen(false);
      setRecoveryOpen(false);
    } catch (err) {
      console.error('Failed to remove recovery contact:', err);
    }
  }

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKey = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      const item = [...navItems, ANNOUNCEMENTS_ITEM].find(n => n.shortcut === e.key.toUpperCase());
      if (item) navigate(item.path);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [navigate]);

  // Close avatar popover on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setPopoverOpen(false);
      }
    };
    if (popoverOpen) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [popoverOpen]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (!menuOpen) return;
    lockScroll();
    return () => unlockScroll();
  }, [menuOpen]);

  return (
    <div className={`${styles.layout}`}>

      {/* ── Recovery session banner ── */}
      {isRecoverySession && (
        <div className={styles.recoveryBanner}>
          <ShieldPlus size={15} />
          Recovery contact view
        </div>
      )}

      {/* ── Desktop sidebar ── */}
      <aside className={styles.sidebar} style={isRecoverySession ? { top: 36, height: 'calc(100vh - 36px)' } : {}}>
        <div className={styles.logoMark}>
          <Logo height={20} />
        </div>

        <nav className={styles.nav}>
          {navItems.map(({ icon: Icon, path, label, shortcut }) => (
            <div key={path} className={styles.navItemWrapper}>
              <NavLink
                to={path}
                className={({ isActive }) =>
                  `${styles.navItem} ${isActive ? styles.navItemActive : ''}`
                }
              >
                <Icon size={18} />
              </NavLink>
              <div className={styles.tooltip}>
                <span className={styles.tooltipLabel}>Go to {label}</span>
                <span className={styles.tooltipKey}>{shortcut}</span>
              </div>
            </div>
          ))}
        </nav>

        <div className={styles.sidebarBottom}>
          <div className={styles.navItemWrapper}>
            <NavLink
              to={ANNOUNCEMENTS_ITEM.path}
              className={({ isActive }) =>
                `${styles.navItem} ${styles.bellLink} ${isActive ? styles.navItemActive : ''}`
              }
              aria-label={unreadCount > 0 ? `Announcements, ${unreadCount} unread` : 'Announcements'}
            >
              <Bell size={18} />
              {unreadCount > 0 && <span className={styles.unreadDot} />}
            </NavLink>
            <div className={styles.tooltip}>
              <span className={styles.tooltipLabel}>Go to {ANNOUNCEMENTS_ITEM.label}</span>
              <span className={styles.tooltipKey}>{ANNOUNCEMENTS_ITEM.shortcut}</span>
            </div>
          </div>

          <button
            className={styles.sunBtn}
            title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            onClick={() => setTheme(t => t === 'dark' ? 'light' : 'dark')}
          >
            {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          </button>

          <div className={styles.avatarWrapper} ref={popoverRef}>
            <button className={styles.avatar} onClick={() => setPopoverOpen(p => !p)}>
              {initial}
            </button>

            {popoverOpen && (
              <div className={styles.popover}>
                {isRecoverySession ? (
                  <>
                    <div className={styles.popoverUser}>
                      <span className={styles.popoverName}>{firstName}'s Recovery Contact</span>
                    </div>
                    <p className={styles.popoverEmail}>{storedUser.recoveryContactEmail}</p>
                    <button
                      className={styles.recoveryBtn}
                      onClick={() => { setPopoverOpen(false); navigate('/dashboard/help?action=permanent-access'); }}
                    >
                      <Send size={15} /> Request permanent access
                    </button>
                    <button className={styles.logoutBtn} onClick={() => { localStorage.removeItem('isRecoverySession'); navigate('/login'); }}>
                      <LogOut size={15} /> Log out
                    </button>
                  </>
                ) : (
                  <>
                    <div className={styles.popoverUser}>
                      {editingName ? (
                        <input
                          ref={nameInputRef}
                          className={styles.popoverNameInput}
                          value={nameValue}
                          onChange={e => setNameValue(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') { e.preventDefault(); saveName(); }
                            if (e.key === 'Escape') { setEditingName(false); setNameValue(fullName); }
                          }}
                          onBlur={saveName}
                          autoFocus
                        />
                      ) : (
                        <span className={styles.popoverName}>{nameValue}</span>
                      )}
                      <button
                        className={styles.popoverEditBtn}
                        onClick={() => { setEditingName(true); setTimeout(() => nameInputRef.current?.focus(), 0); }}
                      >
                        <Pencil size={14} />
                      </button>
                    </div>
                    <p className={styles.popoverEmail}>{userEmail}</p>
                    <button className={styles.recoveryBtn} onClick={openRecovery}>
                      <ShieldPlus size={16} /> {savedRecovery ? 'Edit recovery contact' : 'Set up recovery contact'}
                    </button>
                    <button className={styles.logoutBtn} onClick={() => { localStorage.removeItem('isRecoverySession'); navigate('/login'); }}>
                      <LogOut size={15} /> Log out
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* ── Main ── */}
      <main className={`${styles.main} ${className || ''}`} style={isRecoverySession ? { paddingTop: 36 } : {}}>
        {/* Mobile header */}
        <div className={styles.mobileHeader}>
          <div className={styles.mobilePill}>
            <Logo height={22} />
            <button className={styles.menuToggle} onClick={() => setMenuOpen(true)}>
              <Menu size={22} />
              {unreadCount > 0 && <span className={styles.unreadDot} />}
            </button>
          </div>
        </div>

        <div className={styles.content}>{children}</div>
      </main>

      {/* ── Program agreement modal ── */}
      {showContract && (
        <ContractModal onSigned={() => setShowContract(false)} />
      )}

      {/* ── Recovery contact modal ── */}
      {recoveryOpen && !removeConfirmOpen && (
        <div className={styles.recoveryOverlay} onClick={() => setRecoveryOpen(false)}>
          <div className={styles.recoveryModal} onClick={e => e.stopPropagation()}>
            <div className={styles.recoveryHeader}>
              <h2 className={styles.recoveryTitle}>{savedRecovery ? 'Edit recovery contact' : 'Set up recovery contact'}</h2>
              <button className={styles.recoveryCloseBtn} onClick={() => setRecoveryOpen(false)}><X size={18} /></button>
            </div>
            <div className={styles.recoveryBody}>
              <p className={styles.recoveryDesc}>
                If you're ever unable to access your account, your recovery contact can request temporary access on your behalf. Any withdrawal requests made by them will be notified to your email. From there, they can request permanent access if needed, and our team will review every request carefully before anything is granted.
              </p>
              <p className={styles.recoveryDesc}>
                This is optional and you can update or remove your recovery contact at any time.
              </p>
              <label className={styles.recoveryLabel}>Recovery contact email</label>
              <input
                type="email"
                className={`${styles.recoveryInput} ${recoveryError ? styles.recoveryInputError : ''}`}
                value={recoveryEmail}
                onChange={e => { setRecoveryEmail(e.target.value); setRecoveryError(''); }}
                placeholder=""
              />
              {recoveryError && <p className={styles.recoveryErrorMsg}>{recoveryError}</p>}
            </div>
            <div className={styles.recoveryFooter}>
              {savedRecovery && (
                <button className={styles.recoveryRemoveBtn} onClick={() => setRemoveConfirmOpen(true)}>
                  Remove contact
                </button>
              )}
              <div className={styles.recoveryFooterRight}>
                <button className={styles.recoveryCancelBtn} onClick={() => setRecoveryOpen(false)}>Cancel</button>
                <button className={styles.recoverySaveBtn} onClick={saveRecovery}>
                  {savedRecovery ? 'Save changes' : 'Save recovery contact'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Remove recovery contact confirmation ── */}
      {removeConfirmOpen && (
        <div className={styles.recoveryOverlay} onClick={() => setRemoveConfirmOpen(false)}>
          <div className={styles.recoveryModal} onClick={e => e.stopPropagation()}>
            <div className={styles.recoveryHeader}>
              <h2 className={styles.recoveryTitle}>Remove recovery contact?</h2>
              <button className={styles.recoveryCloseBtn} onClick={() => setRemoveConfirmOpen(false)}><X size={18} /></button>
            </div>
            <div className={styles.recoveryBody}>
              <p className={styles.recoveryDesc}>
                Without a recovery contact, your team may lose permanent access to your account in the event of an emergency.
              </p>
            </div>
            <div className={styles.recoveryFooter}>
              <div className={styles.recoveryFooterRight}>
                <button className={styles.recoveryCancelBtn} onClick={() => setRemoveConfirmOpen(false)}>Cancel</button>
                <button className={styles.recoveryConfirmRemoveBtn} onClick={removeRecovery}>Remove</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Mobile menu overlay ── */}
      {menuOpen && (
        <div className={styles.mobileMenu}>
          <div className={styles.mobileMenuHeader}>
            <div className={styles.mobilePill}>
              <Logo height={22} />
              <button className={styles.menuToggle} onClick={() => setMenuOpen(false)}>
                <X size={22} />
              </button>
            </div>
          </div>

          <nav className={styles.mobileNav}>
            {[...navItems, ANNOUNCEMENTS_ITEM].map(({ icon: Icon, path, label }) => (
              <div key={path} className={styles.mobileNavItemWrapper}>
              <NavLink
                to={path}
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  `${styles.mobileNavItem} ${isActive ? styles.mobileNavItemActive : ''}`
                }
              >
                <Icon size={20} />
                {label}
                {path === ANNOUNCEMENTS_ITEM.path && unreadCount > 0 && (
                  <span className={styles.mobileUnreadDot} />
                )}
              </NavLink>
              </div>
            ))}
          </nav>

          <div className={styles.mobileMenuBottom}>
            <NavLink
              to="/dashboard/account-settings"
              className={styles.mobileMenuBtn}
              onClick={() => setMenuOpen(false)}
            >
              Account settings
            </NavLink>
            <button
              className={`${styles.mobileMenuBtn} ${styles.mobileLogoutBtn}`}
              onClick={() => { setMenuOpen(false); navigate('/login'); }}
            >
              <LogOut size={16} /> Log out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default DashboardLayout;
